import { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { User, AuthTokens, AuthResponse } from '../types';
import { AuthService } from '../services/auth';
import { setLogoutCallback } from '../services/axios';

const authService = new AuthService();

interface AuthContextType {
  user: User | null;
  tokens: AuthTokens | null;
  login: (response: AuthResponse) => void;
  logout: () => void;
  isAuthenticated: boolean;
  refreshTokens: () => Promise<AuthTokens>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedTokens = localStorage.getItem('tokens');

      if (storedUser && storedTokens) {
        const parsedUser = JSON.parse(storedUser);
        const parsedTokens = JSON.parse(storedTokens);

        // Reject corrupt/undefined values that got serialised as strings
        if (parsedUser && parsedTokens && typeof parsedUser === 'object' && typeof parsedTokens === 'object') {
          setUser(parsedUser);
          setTokens(parsedTokens);
          authService.setTokens(parsedTokens);
        } else {
          localStorage.removeItem('user');
          localStorage.removeItem('tokens');
        }
      }
    } catch {
      // Corrupt localStorage data — clear it so the app can recover
      localStorage.removeItem('user');
      localStorage.removeItem('tokens');
    }
  }, []);

  const login = useCallback((response: AuthResponse) => {
    setUser(response.user);
    setTokens(response.tokens);
    authService.setTokens(response.tokens);
    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.setItem('tokens', JSON.stringify(response.tokens));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setTokens(null);
    authService.clearTokens();
    localStorage.removeItem('user');
    localStorage.removeItem('tokens');
  }, []);

  // Регистрируем logout в axios interceptor чтобы синхронизировать React-стейт
  useEffect(() => {
    setLogoutCallback(logout);
  }, [logout]);

  const refreshTokens = useCallback(async () => {
    try {
      const newTokens = await authService.refreshTokens();
      setTokens(newTokens);
      localStorage.setItem('tokens', JSON.stringify(newTokens));
      return newTokens;
    } catch (error: any) {
      // Разлогиниваем только если refresh-токен явно отвергнут сервером.
      // Сетевые сбои и 5xx не должны выкидывать пользователя из аккаунта.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        logout();
      }
      throw error;
    }
  }, [logout]);

  const value = useMemo(() => ({
    user,
    tokens,
    login,
    logout,
    isAuthenticated: !!user,
    refreshTokens,
  }), [user, tokens, login, logout, refreshTokens]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
